/**
 * Catálogo de telas, indexado pela cota.
 *
 * A cota é o código em versalete no canto do cabeçalho — `CMP·01`, `CAD·13`. Ela
 * nasceu como enfeite de prancheta técnica (`.spec/014`) e virou endereço: é por
 * ela que o suporte diz "abra a CMP·03" sem depender de o usuário chamar a tela
 * pelo mesmo nome que nós.
 *
 * **Por que um mapa central e não o literal em cada página.** A cota morava
 * escrita à mão em cada arquivo, duas vezes por entidade — uma na listagem,
 * outra no formulário. Ninguém tinha como ver o conjunto, e o previsível
 * aconteceu: `CAD·12` e `CTL·02` foram atribuídas a duas telas cada uma, e um
 * endereço que aponta para dois lugares não é endereço. Aqui a chave é única por
 * construção (objeto não tem chave repetida) e `cotas.test.ts` varre os fontes
 * cobrando que toda cota escrita numa página esteja registrada.
 *
 * O mapa é também o que torna a **busca do menu** possível: sem ele o `AppShell`
 * conhece rótulo e rota, e a cota vive longe, dentro da página que ninguém abriu
 * ainda.
 *
 * **O console de operação fica de fora**, embora tenha cotas (`OPE·01`,
 * `OPE·02`). Ele é o outro plano de acesso: outro shell, outro login e
 * permissões de `sistema.*`, que `podeFazer` nem enxerga. Misturá-lo aqui faria
 * a busca do cliente oferecer telas que não existem para ele. O que o teste
 * cobra do console é só o formato da cota.
 */

/**
 * O que a tela é, e portanto em que parte da seção ela mora (`.spec/015`,
 * decisão 75).
 *
 * - `painel` — a tela de relance. **Abre** a seção, sempre, fora dos
 *   submenus: é por ela que se decide qual das outras abrir.
 * - `processo` — o que se lança e se consulta no dia a dia: documentos,
 *   atalhos de geração e as leituras do que eles gravaram (cardex, extrato).
 * - `cadastro` — o que se mantém e se abre de vez em quando.
 * - `relatorio` — o que se abre para ler, filtrar e imprimir: não lança nada e
 *   não é a tela de relance. O submenu "Relatórios" aparece sozinho no módulo
 *   que tem algum — o primeiro foi o consumo de insumos na obra (`CTR·05`), em
 *   Contratos.
 *
 * É propriedade da **tela**, e não da posição no menu, pelo mesmo motivo da
 * cota: o menu se reorganiza, a natureza da tela não. A leitura do cardex e do
 * extrato entrou em `processo` porque é consultada junto com o lançamento que
 * a produz; o dia em que existirem relatórios de verdade, ela é a candidata a
 * mudar.
 */
export type Natureza = 'painel' | 'processo' | 'cadastro' | 'relatorio';

export interface Tela {
  /** Como a tela se chama no menu. Curto: a coluna tem 15rem. */
  readonly rotulo: string;
  /** Rota da listagem. Tipada contra a árvore de rotas por causa do `as const`. */
  readonly para: string;
  /** Permissão de ver. Quem não a tem não recebe o item nem o resultado de busca. */
  readonly permissao: string;
  /** Em que parte da seção ela mora. Ver `Natureza`. */
  readonly natureza: Natureza;
}

/**
 * As telas, na numeração por módulo.
 *
 * `CAD` cadastros, `CMP` compras, `CTL` controle de estoque, `FIN` financeiro,
 * `PAG` contas a pagar, `REC` contas a receber, `CTR` contratos e obras, `FAT`
 * faturamento, `ADM` administração da conta.
 *
 * O prefixo é o do **módulo do documento**, não o da seção do menu em que ele
 * aparece: a condição de pagamento é `FIN·05` e mora em Financeiro; a unidade de
 * medida é `CAD·12` e mora em Compras, ao lado do insumo que a usa. O menu se
 * reorganiza conforme o uso; a cota, não — ela é citada em chamado e em
 * treinamento, e renumerar invalidaria o que já foi escrito lá fora.
 *
 * Lacunas (`FIN·03`, `FIN·04`, `CTR·06`) são telas que existiram ou vão
 * existir. Não as reaproveite: o número livre é o próximo do módulo, não o
 * buraco do meio.
 *
 * **A cota muda quando o documento muda de módulo** — e só então. Aconteceu uma
 * vez: a nota de serviço era `CTR·06` e virou `FAT·01` quando o Faturamento
 * passou a ser módulo, com papel próprio (decisão 100). Não é o caso do
 * parágrafo acima: lá o menu se reorganizou e o documento continuou sendo de
 * quem era; aqui o documento trocou de dono, e o prefixo diz o dono.
 */
export const TELAS = {
  'CAD·01': {
    rotulo: 'Empresas',
    para: '/empresas',
    permissao: 'cadastros.empresa.ver',
    natureza: 'cadastro',
  },
  'CAD·02': {
    rotulo: 'Fornecedores',
    para: '/fornecedores',
    permissao: 'cadastros.fornecedor.ver',
    natureza: 'cadastro',
  },
  'CAD·03': {
    rotulo: 'Clientes',
    para: '/clientes',
    permissao: 'cadastros.cliente.ver',
    natureza: 'cadastro',
  },
  'CAD·04': {
    rotulo: 'Plano de contas',
    para: '/contas-contabeis',
    permissao: 'cadastros.conta_contabil.ver',
    natureza: 'cadastro',
  },
  'CAD·05': {
    rotulo: 'Hist. bancários',
    para: '/historicos-bancarios',
    permissao: 'cadastros.historico_bancario.ver',
    natureza: 'cadastro',
  },
  'CAD·06': {
    rotulo: 'Hist. financeiros',
    para: '/historicos-financeiros',
    permissao: 'cadastros.historico_financeiro.ver',
    natureza: 'cadastro',
  },
  'CAD·07': {
    rotulo: 'Contas correntes',
    para: '/contas-correntes',
    permissao: 'cadastros.conta_corrente.ver',
    natureza: 'cadastro',
  },
  'CAD·08': {
    rotulo: 'Grupos de insumos',
    para: '/grupos-insumos',
    permissao: 'cadastros.grupo_insumo.ver',
    natureza: 'cadastro',
  },
  'CAD·09': {
    rotulo: 'Insumos',
    para: '/insumos',
    permissao: 'cadastros.insumo.ver',
    natureza: 'cadastro',
  },
  'CAD·10': {
    rotulo: 'Depósitos',
    para: '/depositos',
    permissao: 'cadastros.deposito.ver',
    natureza: 'cadastro',
  },
  'CAD·11': {
    rotulo: 'Tipos de movimentação',
    para: '/tipos-movimentacao-estoque',
    permissao: 'cadastros.tipo_movimentacao_estoque.ver',
    natureza: 'cadastro',
  },
  'CAD·12': {
    rotulo: 'Unidades de medida',
    para: '/unidades-medida',
    permissao: 'cadastros.unidade_medida.ver',
    natureza: 'cadastro',
  },
  'CAD·13': {
    rotulo: 'Operações fiscais',
    para: '/operacoes-fiscais',
    permissao: 'cadastros.operacao_fiscal.ver',
    natureza: 'cadastro',
  },
  /*
    O de-para do catálogo do fornecedor.

    Cota de **cadastro**, e não de controladoria, embora quem mais o alimente
    seja a conferência de nota por XML: o que ele guarda é vínculo entre dois
    cadastros, e é onde se vem para corrigir — não para lançar. A permissão é
    própria (`cadastros.fornecedor_item.*`) porque Controladoria escreve nela sem
    receber o cadastro inteiro do fornecedor. Ver `.spec/015`, decisão 20.
  */
  'CAD·14': {
    rotulo: 'Itens do fornecedor',
    para: '/fornecedores-itens',
    permissao: 'cadastros.fornecedor_item.ver',
    natureza: 'cadastro',
  },
  /*
    Conversões entre unidades — o fator **sugerido** que o item do fornecedor
    copia. Cadastro do Financeiro, como a unidade de medida a que ele se refere:
    é a mesma decisão de escala, e Compras e Controladoria a recebem só de
    leitura.
  */
  'CAD·15': {
    rotulo: 'Conversões entre unidades',
    para: '/conversoes-unidade-medida',
    permissao: 'cadastros.conversao_unidade.ver',
    natureza: 'cadastro',
  },

  /*
    O vocabulário do comercial: as linhas pelas quais a obra é orçada e vendida,
    antes de existir contrato ou canteiro (`.spec/015`, decisão 58).

    Prefixo `CAD`, e não `CTR`, embora a tela more em Contratos: a regra do mapa
    é que o prefixo segue o **módulo do documento**, e este é um cadastro — a
    mesma razão pela qual a unidade de medida é `CAD·12` e mora em Compras.

    A permissão, ao contrário, é `contratos.`, porque o que decide o namespace é
    **quem mantém a lista**, e quem a mantém é o orçamentista (ver `auth.ts`).
    Recebem-na Contratos por `contratos.*`, Gerência por `*.ver` e o
    Administrador por `*`; o Financeiro fica de fora, que é o ponto.

    **A rota de leitura da API é `autenticado`, e isso não é contradição.** Esta
    permissão recorta o menu e o acesso à tela; o seletor do cadastro de insumo
    lê o catálogo sem ela, porque quem cadastra material não precisa de permissão
    de orçamento para escolher a linha a que o insumo pertence.
  */
  'CAD·16': {
    rotulo: 'Itens de orçamento',
    para: '/itens-orcamento',
    permissao: 'contratos.item_orcamento.ver',
    natureza: 'cadastro',
  },

  'CTR·01': {
    rotulo: 'Contratos',
    para: '/contratos',
    permissao: 'contratos.contrato.ver',
    natureza: 'processo',
  },
  'CTR·02': {
    rotulo: 'Obras',
    para: '/obras',
    permissao: 'contratos.obra.ver',
    natureza: 'processo',
  },
  /*
    O painel confronta os dois lados do caixa **pela obra** e soma o consumo, e
    por isso tem **permissão própria**: `contratos.painel_obra.ver`, e não
    `contratos.obra.ver`.

    Aquela abre o *cadastro* da obra — nome, endereço, cronograma —, e a
    Controladoria, o Pagamento e o Recebimento a recebem só para que o seletor de
    obra do rateio não abra vazio. Derivar o painel dela daria a exposição
    financeira de cada canteiro a quem foi autorizado apenas a escolher uma obra
    numa lista.

    O sufixo é `painel_obra`, e não `painel`: `contratos.painel.ver` sugeriria "o
    painel do módulo", e o dia em que existir um painel de contratos — medição,
    vigência, saldo a executar — os dois brigariam pelo mesmo nome.

    São três papéis, cada um por um caminho: Administrador (`*`), Gerência
    (`*.ver`) e Contratos (`contratos.*`) — sem linha nova em
    `PERMISSOES_POR_PAPEL`. O teste de `auth.test.ts` é onde isso está travado,
    contraprovas incluídas: Financeiro e Controladoria ficam de fora.
  */
  'CTR·03': {
    rotulo: 'Painel de obras',
    para: '/painel-obras',
    permissao: 'contratos.painel_obra.ver',
    natureza: 'painel',
  },

  /*
    O orçamento é o **primeiro documento do processo**, e a cota registra isso:
    ele existe antes do contrato e antes da obra (`.spec/015`, decisão 58).

    Numerado `CTR·04`, e não inserido na frente do contrato — a cota é endereço
    citado em chamado e em treinamento, e renumerar invalidaria o que já foi
    escrito lá fora. Quem diz a ordem do processo é a posição no menu, não o
    número.

    A permissão entra pelo curinga `contratos.*` do papel Contratos, como o
    contrato e a obra: são o mesmo caminho comercial do mesmo negócio, e é a
    mesma mão que percorre os três.
  */
  'CTR·04': {
    rotulo: 'Orçamentos',
    para: '/orcamentos',
    permissao: 'contratos.orcamento.ver',
    natureza: 'processo',
  },

  /*
    O primeiro relatório do sistema, e é o que estreia o submenu "Relatórios":
    não há mais nada a fazer para ele aparecer além da `natureza`.

    **Permissão própria**, `contratos.relatorio_consumo.ver`, pela razão do
    painel de obras: o relatório mostra quanto cada canteiro gastou em cada
    insumo, e `contratos.obra.ver` é dada a quem só precisa escolher uma obra
    num seletor de rateio. Chega aos mesmos três papéis do painel — Admin (`*`),
    Gerência (`*.ver`) e Contratos (`contratos.*`) — sem linha nova em
    `PERMISSOES_POR_PAPEL`.

    A rota fica sob `/relatorios/`, e não sob `/obras/`: `telaAberta` acenderia
    o item "Obras" do menu com o relatório aberto, e o usuário veria duas telas
    abertas ao mesmo tempo.
  */
  'CTR·05': {
    rotulo: 'Consumo de insumos na obra',
    para: '/relatorios/consumo-insumos-obra',
    permissao: 'contratos.relatorio_consumo.ver',
    natureza: 'relatorio',
  },

  /*
    `CTR·06` é **lacuna** desde 07/10/2026 (`.spec/015`, decisão 100): foi a
    cota da nota de serviço enquanto ela era de Contratos e Obras. A nota mudou
    de módulo e a cota foi atrás — é `FAT·01`, no fim deste mapa. Não a
    reaproveite: o próximo número de Contratos é `CTR·07`.
  */

  'CMP·01': {
    rotulo: 'Solicitações',
    para: '/solicitacoes-compra',
    permissao: 'compras.solicitacao.ver',
    natureza: 'processo',
  },
  'CMP·02': {
    rotulo: 'Cotações de preço',
    para: '/cotacoes-preco',
    permissao: 'compras.cotacao.ver',
    natureza: 'processo',
  },
  'CMP·03': {
    rotulo: 'Pedidos de compra',
    para: '/pedidos-compra',
    permissao: 'compras.pedido.ver',
    natureza: 'processo',
  },
  // Atalho que gera pedido a partir da solicitação. A permissão é a de **criar**,
  // porque é o que a tela faz: quem só consulta não a vê no menu nem na busca.
  'CMP·04': {
    rotulo: 'Gerar pedidos',
    para: '/gerar-pedidos',
    permissao: 'compras.pedido.criar',
    natureza: 'processo',
  },
  /*
    O painel soma os três documentos do módulo, e por isso tem **permissão
    própria** — `compras.painel.ver`, e não a de ver solicitação. Conceder por uma
    das três faria quem só enxerga pedidos ler o número de cotações abertas, que é
    informação da mesa do comprador. Hoje a recebem Administrador, Gerência e
    Compras (`PERMISSOES_POR_PAPEL`, em `contracts/auth.ts`).
  */
  'CMP·05': {
    rotulo: 'Painel de compras',
    para: '/painel-compras',
    permissao: 'compras.painel.ver',
    natureza: 'painel',
  },
  /*
    O caminho curto da compra (`.spec/015`, decisão 46).

    Cota própria, e não uma aba da solicitação: os dois documentos respondem
    perguntas diferentes — a solicitação abre um saldo que a cotação e o pedido
    consomem aos poucos, a lista vira pedido de uma vez — e o suporte precisa
    poder dizer "abra a CMP·06" sem explicar em qual aba clicar.

    A permissão é **própria** (`compras.lista_compra_direta.ver`), e não a de
    solicitação: quem monta a lista é o canteiro, e a linha separada é o que
    permite dar um acesso sem o outro no dia em que isso for pedido.
  */
  'CMP·06': {
    rotulo: 'Listas de compra direta',
    para: '/listas-compra-direta',
    permissao: 'compras.lista_compra_direta.ver',
    natureza: 'processo',
  },
  // O atalho que transforma as listas em pedido. Mesma lógica da CMP·04: pede a
  // permissão de **criar pedido**, porque é o que a tela faz — quem só monta
  // listas não a vê no menu nem na busca.
  'CMP·07': {
    rotulo: 'Gerar pedidos das listas',
    para: '/gerar-pedidos-de-listas',
    permissao: 'compras.pedido.criar',
    natureza: 'processo',
  },
  /*
    A mesa do autorizador (`.spec/015`, decisão 109). `compras.aprovacao.fila`
    não sai de curinga nenhum de Compras nem do `*.ver` da Gerência — sai do
    `compras.aprovacao.*` do Autorizador de compras e do `*` do administrador.
  */
  /*
    A mesa de quem lança (`.spec/015`, decisão 110), irmã da `CMP·08`.
    `compras.envio_aprovacao.*` é do papel Compras e do administrador; o
    autorizador não a tem, porque não lança.
  */
  'CMP·09': {
    rotulo: 'Enviar para aprovação',
    para: '/envio-aprovacao-compra',
    permissao: 'compras.envio_aprovacao.fila',
    natureza: 'processo',
  },
  'CMP·08': {
    rotulo: 'Aprovações pendentes',
    para: '/aprovacoes-compra',
    permissao: 'compras.aprovacao.fila',
    natureza: 'processo',
  },
  /*
    O envio dos pedidos por e-mail em lote (`.spec/015`, decisão 116). A
    permissão é a de enviar, `compras.pedido.enviar`, que o papel Compras tem
    pelo curinga `compras.pedido.*`: a tela é o mesmo gesto do botão E-mail do
    pedido, feito para vários.
  */
  'CMP·10': {
    rotulo: 'Enviar pedidos por e-mail',
    para: '/envio-pedidos-compra',
    permissao: 'compras.pedido.enviar',
    natureza: 'processo',
  },

  'CTL·01': {
    rotulo: 'Lançamentos de estoque',
    para: '/lancamentos-estoque',
    permissao: 'controladoria.estoque.ver',
    natureza: 'processo',
  },
  'CTL·02': {
    rotulo: 'Notas de compra',
    para: '/notas-compra',
    permissao: 'controladoria.nota.ver',
    natureza: 'processo',
  },
  // Mesma lógica da CMP·04: é o atalho de entrada, e pede a permissão de criar.
  'CTL·03': {
    rotulo: 'Entrada por pedido',
    para: '/notas-compra/por-pedido',
    permissao: 'controladoria.nota.criar',
    natureza: 'processo',
  },
  'CTL·04': {
    rotulo: 'Cardex',
    para: '/cardex',
    permissao: 'controladoria.estoque.ver',
    natureza: 'processo',
  },
  /*
    A terceira leitura de estoque, e a permissão é a mesma das outras duas: as
    três leem o mesmo dado por eixos diferentes, e o papel que recebesse uma sem
    as outras veria o movimento na listagem e um 403 ao perguntar o saldo.

    Cota própria, e não uma aba do cardex: lá se fixa um material e se abre o
    tempo; aqui se fixa um depósito e se abre o catálogo. O suporte precisa poder
    dizer "abra a CTL·07" sem explicar em qual aba clicar.
  */
  'CTL·07': {
    rotulo: 'Estoque do depósito',
    para: '/estoque-deposito',
    permissao: 'controladoria.estoque.ver',
    natureza: 'processo',
  },
  /*
    O segundo atalho de entrada de nota, ao lado da `CTL·03`, e pela mesma lógica:
    pede a permissão de **criar**, porque é isso que ele faz.

    Cota própria, e não uma aba dentro da entrada por pedido: as duas partem de
    coisas diferentes — lá o usuário escolhe pedidos, aqui ele escolhe um arquivo
    — e o suporte precisa poder dizer "abra a CTL·05" sem explicar em qual aba
    clicar. As duas terminam no mesmo formulário de nota (`.spec/015`, decisão 19).
  */
  'CTL·05': {
    rotulo: 'Entrada por XML',
    para: '/notas-compra/por-xml',
    permissao: 'controladoria.nota.criar',
    natureza: 'processo',
  },
  /*
    Transferência entre depósitos — na prática, entre obras.

    Cota de controladoria, e não de consumo: o material não vira custo de canteiro
    nenhum aqui, ele muda de lugar. A permissão é **própria**
    (`controladoria.transferencia.*`), e não `controladoria.estoque.*`: lançar
    perda num galpão e decidir que material muda de obra são decisões de tamanhos
    diferentes. Ver `PERMISSOES_POR_PAPEL`, em `contracts/auth.ts`.
  */
  'CTL·06': {
    rotulo: 'Transferências',
    para: '/transferencias-estoque',
    permissao: 'controladoria.transferencia.ver',
    natureza: 'processo',
  },

  /*
    Prefixo próprio, e não `CTL·05`: o consumo é módulo em `.spec/010` — "acesso
    ao módulo de nota de compra" e "acesso ao módulo de consumo" são duas linhas
    separadas —, e a permissão dele é `consumo.*`, fora de `controladoria.*`. A
    cota é o endereço do documento, e o prefixo diz de que módulo ele é; dar-lhe
    um `CTL` faria o suporte procurá-lo no papel errado no dia em que os dois
    acessos forem concedidos separadamente.

    Ele mora na seção **Estoque** do menu assim mesmo. É onde o usuário o procura
    — a saída para a obra é a última perna do material —, e uma seção com um item
    só custaria uma linha de título para não agrupar nada.
  */
  'CSM·01': {
    rotulo: 'Consumo de obra',
    para: '/lancamentos-consumo',
    permissao: 'consumo.lancamento.ver',
    natureza: 'processo',
  },
  /*
    A distribuição parte do **depósito**, e não da obra: o material saiu sem
    destino certo e é repartido depois entre obras e etapas. Grava consumos
    comuns, um por obra — por isso a permissão é a de **criar consumo**, e a cota
    é do mesmo módulo. Tela separada, e não um modo da CSM·01: lá a obra é o
    cabeçalho, aqui é cada linha, e o suporte precisa poder dizer "abra a
    CSM·02" sem explicar qual botão alterna o formulário.
  */
  'CSM·02': {
    rotulo: 'Distribuir consumo',
    para: '/distribuicao-consumo',
    permissao: 'consumo.lancamento.criar',
    natureza: 'processo',
  },
  /*
    A devolução é o caminho de volta do consumo: dá entrada num depósito e abate
    o custo da obra (`.spec/015`, decisão 82). Documento próprio, e não um modo
    da CSM·01, pelo mesmo motivo da distribuição.
  */
  'CSM·03': {
    rotulo: 'Devolução de consumo',
    para: '/devolucoes-consumo',
    permissao: 'consumo.devolucao.ver',
    natureza: 'processo',
  },

  'FIN·01': {
    rotulo: 'Lançamentos em c/c',
    para: '/lancamentos-conta-corrente',
    permissao: 'financeiro.lancamento_conta_corrente.ver',
    natureza: 'processo',
  },
  /*
    `FIN·07`, e não `FIN·03` nem `FIN·04`.

    As duas lacunas são telas que existiram ou vão existir, e o comentário no topo
    do mapa já diz o que fazer com elas: o número livre é o **próximo** do módulo,
    não o buraco do meio. Reaproveitar um invalidaria o que já foi escrito em
    chamado e em treinamento sobre a tela antiga.

    Permissão **própria**, e não a do lançamento manual: transferir move dinheiro
    entre contas do grupo, e quem lança uma tarifa bancária não é necessariamente
    quem decide que caixa reforça qual. O papel Financeiro recebe as duas hoje
    pelo curinga `financeiro.*`, sem linha nova em `PERMISSOES_POR_PAPEL`; a
    separação existe para o dia em que não for.
  */
  'FIN·07': {
    rotulo: 'Transf. entre contas',
    para: '/transferencias-conta-corrente',
    permissao: 'financeiro.transferencia_conta_corrente.ver',
    natureza: 'processo',
  },
  'FIN·02': {
    rotulo: 'Extrato',
    para: '/extrato-conta-corrente',
    permissao: 'financeiro.movimentacao_conta_corrente.ver',
    natureza: 'processo',
  },
  'FIN·05': {
    rotulo: 'Cond. pagamento',
    para: '/condicoes-pagamento',
    permissao: 'cadastros.condicao_pagamento.ver',
    natureza: 'cadastro',
  },
  /*
    O painel que confronta as duas pontas do caixa, e a **única** tela de painel
    cuja permissão não é do módulo que ela lê: `financeiro.painel.ver`, e não
    `pagamento.painel.ver` nem `recebimento.painel.ver`.

    Exigir uma das duas daria o confronto a quem só enxerga um lado — quem paga
    leria a carteira de cobrança inteira, e é justamente a assimetria que PAG·02
    e REC·02 travam. De `financeiro.`, então, porque é a pergunta de quem projeta
    o caixa.

    São três papéis, cada um por um caminho: Administrador (`*`), Gerência
    (`*.ver`) e Financeiro (`financeiro.*`) — sem linha nova em
    `PERMISSOES_POR_PAPEL`. O teste de `auth.test.ts` é onde isso está travado,
    contraprovas incluídas.
  */
  'FIN·06': {
    rotulo: 'Painel financeiro',
    para: '/painel-financeiro',
    permissao: 'financeiro.painel.ver',
    natureza: 'painel',
  },

  'PAG·01': {
    rotulo: 'Contas a pagar',
    para: '/contas-pagar',
    permissao: 'pagamento.conta_pagar.ver',
    natureza: 'processo',
  },
  /*
    O agrupamento de títulos (decisão 119): títulos do mesmo fornecedor trocados
    por novos que fecham o mesmo total. Cota própria, e não um botão na `PAG·01`:
    o agrupamento é documento numerado, que se consulta e se desfaz depois.
    Permissão própria (`pagamento.agrupamento.ver`), que o papel Pagamento
    alcança por `pagamento.*` e a Gerência por `*.ver`.
  */
  'PAG·05': {
    rotulo: 'Agrupamento de títulos',
    para: '/agrupamentos-titulos',
    permissao: 'pagamento.agrupamento.ver',
    natureza: 'processo',
  },
  /*
    O painel mostra a exposição inteira do grupo — quanto se deve, o que vence
    nesta semana e a quem se deve mais —, e por isso tem **permissão própria**:
    `pagamento.painel.ver`, e não a de ver título.

    A permissão própria é o que permite conceder o painel **sem** conceder o
    módulo, e é exatamente o que o Financeiro recebe: ele projeta o caixa e
    precisa do número, mas não lista título nem dá baixa (`.spec/010` separa as
    duas mãos). Pelo caminho inverso, ela impede que ver o painel saia de
    `pagamento.conta_pagar.ver` — quem lança uma conta de energia não lê o total
    devido da empresa.

    São quatro papéis, cada um por um caminho: Administrador (`*`), Gerência
    (`*.ver`), Pagamento (`pagamento.*`) e Financeiro, por linha escrita à mão em
    `PERMISSOES_POR_PAPEL`. O teste de `auth.test.ts` é onde isso está travado.
  */
  'PAG·02': {
    rotulo: 'Painel de contas a pagar',
    para: '/painel-contas-pagar',
    permissao: 'pagamento.painel.ver',
    natureza: 'painel',
  },
  /*
    O relatório do pagar: os títulos por empresa e vencimento, com subtotal por
    dia, para imprimir.

    **Permissão própria**, `pagamento.relatorio_contas_pagar.ver`, pela razão do
    CTR·05: o relatório entrega a carteira inteira somada e pronta para sair da
    empresa, e isso se concede de propósito, não por tabela com a permissão de
    abrir um título. Chega a Admin (`*`), Gerência (`*.ver`) e Pagamento
    (`pagamento.*`) sem linha nova em `PERMISSOES_POR_PAPEL`; o Financeiro, que
    recebe o painel à mão, fica de fora (ver `auth.test.ts`).

    A rota fica sob `/relatorios/`, e não sob `/contas-pagar/`: `telaAberta`
    acenderia "Contas a pagar" com o relatório aberto. Com três telas, a seção
    Pagamento continua **plana** (`MINIMO_PARA_SUBMENUS`): o relatório entra na
    lista corrida, e o submenu "Relatórios" só aparece quando a seção crescer.
  */
  'PAG·03': {
    rotulo: 'Relatório de contas a pagar',
    para: '/relatorios/contas-pagar',
    permissao: 'pagamento.relatorio_contas_pagar.ver',
    natureza: 'relatorio',
  },
  /*
    O relatório de pagamentos: as **baixas**, e não os títulos — o que saiu do
    caixa em cada dia, com bruto, desconto, juros, outros e líquido.

    **Permissão própria**, `pagamento.relatorio_pagamentos.ver`, pela razão do
    PAG·03: é o caixa de saída inteiro, somado e pronto para sair da empresa.
    Chega a Admin (`*`), Gerência (`*.ver`) e Pagamento (`pagamento.*`) sem
    linha nova em `PERMISSOES_POR_PAPEL`; o Financeiro fica de fora (ver
    `auth.test.ts`).

    Sob `/relatorios/`, como o PAG·03. Com quatro telas a seção Pagamento era
    plana; com o agrupamento (`PAG·05`) ela chegou a cinco e ganhou os submenus
    Processos e Relatórios (`MINIMO_PARA_SUBMENUS`) — o "quando a seção crescer"
    do comentário do PAG·03.
  */
  'PAG·04': {
    rotulo: 'Relatório de pagamentos',
    para: '/relatorios/pagamentos',
    permissao: 'pagamento.relatorio_pagamentos.ver',
    natureza: 'relatorio',
  },

  'REC·01': {
    rotulo: 'Contas a receber',
    para: '/contas-receber',
    permissao: 'recebimento.conta_receber.ver',
    natureza: 'processo',
  },
  /*
    Espelho de PAG·02, com a mesma razão de ter **permissão própria**
    (`recebimento.painel.ver`): é ela que permite conceder o painel ao Financeiro
    sem conceder a listagem de títulos e a baixa, e que impede ver o painel de
    sair de `recebimento.conta_receber.ver` — quem digita uma cobrança não lê a
    carteira inteira da empresa.

    Quatro papéis, cada um por um caminho: Administrador (`*`), Gerência
    (`*.ver`), Recebimento (`recebimento.*`) e Financeiro, por linha escrita à
    mão em `PERMISSOES_POR_PAPEL`. Pagamento **não** entra: quem paga não vê a
    carteira de cobrança, e é a assimetria que `auth.test.ts` trava.
  */
  'REC·02': {
    rotulo: 'Painel de contas a receber',
    para: '/painel-contas-receber',
    permissao: 'recebimento.painel.ver',
    natureza: 'painel',
  },
  /*
    O relatório do receber, espelho do PAG·03: os títulos por empresa e
    vencimento, com subtotal por dia, e a obra e o contrato de cada um.

    **Permissão própria**, `recebimento.relatorio_contas_receber.ver`, pela razão
    do PAG·03: a carteira de cobrança inteira, somada e pronta para sair da
    empresa, se concede de propósito. Chega a Admin (`*`), Gerência (`*.ver`) e
    Recebimento (`recebimento.*`) sem linha nova em `PERMISSOES_POR_PAPEL`; o
    Financeiro e o Pagamento ficam de fora (ver `auth.test.ts`).

    Sob `/relatorios/`, e não sob `/contas-receber/`, para `telaAberta` não
    acender "Contas a receber" com o relatório aberto. Com três telas, a seção
    Recebimento continua **plana** (`MINIMO_PARA_SUBMENUS`).
  */
  'REC·03': {
    rotulo: 'Relatório de contas a receber',
    para: '/relatorios/contas-receber',
    permissao: 'recebimento.relatorio_contas_receber.ver',
    natureza: 'relatorio',
  },
  /*
    O relatório de recebimentos, espelho do PAG·04: as baixas de título a
    receber — o que entrou no caixa em cada dia —, com a obra e o contrato de
    cada título.

    **Permissão própria**, `recebimento.relatorio_recebimentos.ver`, pela razão
    do REC·03. Chega a Admin, Gerência e Recebimento pelos curingas; Financeiro
    e Pagamento ficam de fora (ver `auth.test.ts`). Com quatro telas, a seção
    Recebimento continua plana.
  */
  'REC·04': {
    rotulo: 'Relatório de recebimentos',
    para: '/relatorios/recebimentos',
    permissao: 'recebimento.relatorio_recebimentos.ver',
    natureza: 'relatorio',
  },

  /*
    A nota fiscal de serviço emitida (`.spec/015`, decisões 90 e 100): o
    documento com que a construtora cobra o serviço que prestou, e de onde o
    título a receber nasce.

    Prefixo `FAT`, o do módulo de **Faturamento**. Foi `CTR·06` até 07/10/2026,
    com o argumento de que faturar é o último passo de Contratos e Obras e de
    que quem a lança é quem acompanha a obra. O Adriano definiu que não é a
    mesma mão: o módulo é próprio, com papel próprio, e vai receber também a
    nota de produto (`FAT·02`, quando existir). Continua não sendo `REC`: a
    nota é do Faturamento, e o título que sai dela é do Recebimento — a mesma
    separação de `CTL·02`, a nota de compra, com o título a pagar.

    **Processo, e não cadastro**: é documento com situação e atos — emitir,
    cancelar.

    A permissão é `faturamento.nota_servico.ver`: Administrador (`*`),
    Faturamento (`faturamento.*`) e, só para consulta, Gerência (`*.ver`). O
    papel Contratos não a tem. Emitir e cancelar têm sufixo próprio
    (`.emitir`, `.cancelar`): quem só confere não produz efeito fiscal.
  */
  'FAT·01': {
    rotulo: 'Notas de serviço',
    para: '/notas-servico',
    permissao: 'faturamento.nota_servico.ver',
    natureza: 'processo',
  },

  'ADM·01': {
    rotulo: 'Usuários',
    para: '/usuarios',
    permissao: 'admin.usuario.ver',
    natureza: 'cadastro',
  },
  'ADM·02': {
    rotulo: 'Configuração',
    para: '/conta',
    permissao: 'admin.conta.ver',
    natureza: 'cadastro',
  },
  /*
    Permissão própria, e não `admin.conta.ver`: hoje só o ADMIN tem as duas,
    pelo curinga, mas elas respondem a perguntas diferentes. Ler o log é
    enxergar tudo o que todo mundo fez, inclusive o financeiro que a pessoa não
    alcança nas telas — e o dia em que algum papel receber a configuração da
    conta não pode ser o dia em que ele passa a ler a auditoria de todos.
  */
  'ADM·03': {
    rotulo: 'Log de alterações',
    para: '/log-alteracoes',
    permissao: 'admin.log_alteracoes.ver',
    natureza: 'processo',
  },
  /*
    `autenticado`, e não uma permissão de papel: é a credencial da própria
    pessoa — senha e segundo fator —, e todo usuário da conta a alcança. É o
    mesmo literal que a API usa para "exige sessão, não exige papel", e
    `podeFazer` o reconhece.
  */
  'ADM·04': {
    rotulo: 'Minha conta',
    para: '/minha-conta',
    permissao: 'autenticado',
    natureza: 'cadastro',
  },
} as const satisfies Record<string, Tela>;

export type Cota = keyof typeof TELAS;

/** União dos caminhos registrados. É o que deixa o `to` do `Link` continuar tipado. */
export type Rota = (typeof TELAS)[Cota]['para'];

/**
 * O menu, agrupado pelos módulos de `.spec/010`.
 *
 * Guarda **cotas**, não cópias de rótulo e rota: assim a tela tem um cadastro
 * só, e mudar o nome dela no menu é mudar uma linha do `TELAS`.
 *
 * As seções existem a partir do quinto item: até quatro, uma lista corrida se
 * varre de relance; acima disso o usuário passa a procurar, e procurar numa
 * lista plana é mais lento do que pular para o grupo certo.
 *
 * **Dentro da seção, a ordem daqui é a do submenu**, não a da tela: o painel
 * abre a seção, e o resto se divide em Processos, Cadastros e Relatórios pela
 * `natureza` de cada tela (ver `montarSecao`). Os favoritos não moram na seção:
 * têm menu próprio, acima de todas (`favoritosDoMenu`, decisão 105). Por isso "movimento antes de cadastro" continua valendo
 * aqui — é a ordem dentro de cada submenu.
 *
 * A ordem dentro da seção é a do negócio, não a alfabética: **movimento antes de
 * cadastro**, porque o lançamento é diário e o grupo se abre uma vez por
 * trimestre; e o atalho **depois** da listagem a que ele leva, porque quem abre o
 * menu procurando "pedidos" quer a lista.
 */
export const MODULOS = [
  /*
    **Pessoa**, e não "Cadastros" (`.spec/015`, decisão 75).
    A seção se chamava Cadastros e guardava só as três pessoas — empresa,
    fornecedor e cliente —, enquanto os outros treze cadastros moravam nos
    módulos que os usam. O nome prometia o que a seção não tinha. Com o submenu
    "Cadastros" dentro de cada módulo, o título solto viraria a segunda porta
    para a mesma palavra; a seção passou a dizer o que ela é.
  */
  { titulo: 'Pessoa', cotas: ['CAD·01', 'CAD·02', 'CAD·03'] },
  {
    // Seção própria, e não um item dentro de Pessoa: contrato é módulo em
    // `.spec/010`, com papel próprio. A obra vem depois do contrato, que é a quem
    // ela pertence — as partes da obra não têm item, são editadas dentro do
    // formulário dela.
    titulo: 'Contratos',
    /*
      O painel **abre** a seção, como em Compras, Pagamento, Recebimento e
      Financeiro: é a tela de relance, e é por ela que se decide qual das outras
      abrir. Pô-lo depois faria quem abre o menu para "ver como está a obra"
      passar pelo contrato e pelo cadastro dela antes.
    */
    /*
      Os documentos na ordem do processo, e não na da numeração: **orçamento,
      contrato, obra**. É a sequência em que eles acontecem — orça-se, contrata-se,
      abre-se o canteiro — e é a ordem em que quem percorre o módulo os procura.
      O painel continua abrindo a seção, como nos demais módulos.

      O catálogo de itens fecha a seção, depois dos documentos: é cadastro, e
      cai no submenu Cadastros de Contratos, e não junto das pessoas, porque
      quem o mantém é quem orça — a mesma mão que abre o contrato e a obra.

      A nota de serviço **saiu daqui** em 07/10/2026 (decisão 100): fechava os
      documentos, depois da obra, e hoje abre a seção Faturamento, logo abaixo.
    */
    cotas: ['CTR·03', 'CTR·04', 'CTR·01', 'CTR·02', 'CAD·16', 'CTR·05'],
  },
  {
    /*
      Seção própria (`.spec/015`, decisão 100), pelo motivo de Pagamento e de
      Recebimento: o papel Faturamento não recebe `contratos.*`, e a nota dentro
      de Contratos faria a seção inteira aparecer para ele só por causa de um
      item — e sumir para o papel Contratos o item que ele não abre mais.

      **Logo depois de Contratos**: a nota aponta a obra e o contrato, e é o
      passo seguinte do mesmo caminho — orça-se, contrata-se, executa-se,
      fatura-se. O título que ela gera aparece mais abaixo, em Recebimento.

      Uma tela só, por enquanto. A nota de produto entra aqui quando existir.
    */
    titulo: 'Faturamento',
    cotas: ['FAT·01'],
  },
  {
    // A cotação logo abaixo da solicitação, e o pedido fechando: é a ordem em que
    // a compra acontece — a obra pede, o comprador cota, o pedido nasce da
    // proposta escolhida. Grupo, unidade e insumo vêm no fim, e nessa ordem,
    // porque é a ordem em que o cadastro se monta.
    titulo: 'Compras',
    /*
      O painel **abre** a seção, e é a única exceção à regra "movimento antes de
      cadastro, e a listagem antes do atalho que leva a ela": ele não é nem
      movimento nem cadastro — é a leitura dos três, e é o que se olha antes de
      decidir qual das listagens abrir. Pô-lo depois de CMP·04 faria a tela de
      relance ficar abaixo dos documentos que ela resume, e quem abre o menu para
      "ver como está" passaria por três telas antes.
    */
    // O de-para do fornecedor fecha a seção, depois do insumo: ele liga o
    // catálogo de quem vende ao cadastro daqui, e só faz sentido depois dos dois.
    cotas: [
      'CMP·05',
      'CMP·01',
      'CMP·02',
      'CMP·03',
      'CMP·04',
      /*
        O caminho curto fecha os documentos, depois do longo e na mesma forma:
        a listagem, e logo abaixo o atalho que a transforma em pedido.

        Depois de CMP·04, e não junto da solicitação, embora a lista seja o
        começo do caminho como a solicitação é do outro. A ordem da seção é a da
        compra que acontece todo dia — pedir, cotar, pedir ao fornecedor,
        gerar —, e intercalar o atalho da compra direta no meio dela faria quem
        segue a sequência normal tropeçar num documento que não usa.
      */
      'CMP·06',
      'CMP·07',
      // As duas filas da autorização fecham os processos, na ordem do ciclo:
      // quem lança envia (CMP·09), quem autoriza decide (CMP·08). Cada papel vê
      // só a sua.
      'CMP·09',
      'CMP·08',
      // Depois da aprovação, que é o que a libera: o pedido só vai ao
      // fornecedor aprovado, quando a conta exige.
      'CMP·10',
      'CAD·08',
      'CAD·12',
      // A conversão logo depois da unidade, que é a quem ela se refere.
      'CAD·15',
      'CAD·09',
      'CAD·14',
    ],
  },
  {
    /*
      Estoque ganhou seção própria com o segundo item. O depósito morava em
      Compras, e o comentário de lá dizia por quê: uma seção com um item só
      custaria uma linha de título para não agrupar nada.

      A nota de compra abre a seção, e não fica sob "Compras" — que é a vizinhança
      que o nome sugere. Quem a digita é a Controladoria, e não o comprador: são
      papéis diferentes em `.spec/010`. E o efeito dela é de estoque — confirmada,
      é ela que dá entrada no depósito e forma o custo médio. Deixá-la em Compras
      mandaria quem confere o saldo procurar a origem dele no menu de comprar.

      O cardex logo depois do lançamento: é a leitura do que o lançamento
      escreveu, e as duas telas se usam na mesma sessão.
    */
    titulo: 'Estoque',
    // O consumo logo depois do lançamento manual: os dois são saída de material,
    // e a diferença entre eles é a obra. Antes do cardex, que é a leitura do que
    // os dois escreveram.
    // Os dois atalhos de entrada juntos, logo depois da listagem que eles
    // alimentam: quem chega com material na mão escolhe entre "veio de pedido" e
    // "tenho o arquivo", e as duas perguntas se fazem no mesmo instante.
    // A transferência fecha as saídas, depois do consumo: as três são movimento
    // de material — entrada, saída para a obra e mudança de canteiro —, e o
    // cardex vem em seguida porque é a leitura do que as três escreveram.
    cotas: [
      'CTL·02',
      'CTL·03',
      'CTL·05',
      'CTL·01',
      'CSM·01',
      // A distribuição logo depois do consumo: grava o mesmo documento, pela
      // outra ponta.
      'CSM·02',
      'CSM·03',
      'CTL·06',
      'CTL·04',
      // O inventário logo depois do cardex: as duas são leitura do que os
      // documentos acima escreveram, e se usam na mesma sessão — o cardex
      // explica a linha que o inventário mostrou fora do lugar.
      'CTL·07',
      'CAD·10',
      'CAD·11',
    ],
  },
  {
    // Seção própria, e não um item dentro de Financeiro: o papel Pagamento não
    // recebe `financeiro.*` (`.spec/010`), e agrupá-los faria a seção inteira
    // sumir para quem só paga — que é justamente quem usa esta tela.
    titulo: 'Pagamento',
    /*
      O painel primeiro, como em Compras: é a tela de relance, e é por ela que se
      decide o que fazer na listagem. Pô-lo depois faria quem abre o menu para
      "ver como está" passar pela listagem inteira antes.
    */
    // O relatório no fim: é o que se abre depois de conferir a listagem, para
    // imprimir o que ela mostrou.
    // O de pagamentos depois do de títulos: primeiro o que se deve, depois o que
    // já saiu.
    // O agrupamento logo depois da listagem: ele parte dos títulos dela.
    cotas: ['PAG·02', 'PAG·01', 'PAG·05', 'PAG·03', 'PAG·04'],
  },
  {
    // Separada de Pagamento pelo mesmo motivo, no sentido inverso: os dois papéis
    // são distintos de propósito, e quem só recebe não enxerga nada da seção do
    // outro. Agrupá-los faria o título mentir para os dois.
    titulo: 'Recebimento',
    // O painel primeiro, como em Pagamento e em Compras: é a tela de relance, e
    // é por ela que se decide o que fazer na listagem.
    // O relatório no fim, como em Pagamento.
    // O de recebimentos depois do de títulos, como em Pagamento.
    cotas: ['REC·02', 'REC·01', 'REC·03', 'REC·04'],
  },
  {
    /*
      A operação fiscal fica aqui, e não em Estoque ao lado do tipo de
      movimentação — que é a vizinhança que o nome sugere. O que ela decide é
      quanto imposto sai do custo de estoque, e isso é decisão fiscal: quem a
      cadastra é o Financeiro. Controladoria, que digita a nota, só a lê.
    */
    titulo: 'Financeiro',
    /*
      O painel **abre** a seção, como em Compras, Pagamento e Recebimento: é a
      tela de relance, e é por ela que se decide o que fazer nas outras. Pô-lo
      depois faria quem abre o menu para "ver como está o caixa" passar pelo
      lançamento e pelo extrato antes.
    */
    /*
      A transferência entre o lançamento e o extrato: as duas primeiras são o que
      se escreve no caixa, e o extrato é a leitura do que as duas escreveram. É a
      mesma ordem que Estoque usa — lançamento, consumo, transferência, cardex —,
      e pelo mesmo motivo.
    */
    cotas: [
      'FIN·06',
      'FIN·01',
      'FIN·07',
      'FIN·02',
      'CAD·04',
      'CAD·07',
      'CAD·05',
      'CAD·06',
      'FIN·05',
      'CAD·13',
    ],
  },
  // O log fica por último: é o que se abre depois de o resto ter acontecido, e
  // é a única tela do módulo que não configura nada.
  // "Minha conta" fecha a seção: é a única tela dela que todo usuário vê, e
  // para quem não é administrador a seção inteira é só ela.
  { titulo: 'Administração', cotas: ['ADM·01', 'ADM·02', 'ADM·03', 'ADM·04'] },
] as const satisfies readonly { titulo: string; cotas: readonly Cota[] }[];

/** Uma tela pronta para virar item de menu ou linha de resultado. */
export interface ItemDeTela extends Tela {
  readonly cota: Cota;
  readonly para: Rota;
  /** Seção em que ela mora. É o que dá contexto ao resultado solto da busca. */
  readonly modulo: string;
}

function itensDoModulo(modulo: (typeof MODULOS)[number]): ItemDeTela[] {
  return modulo.cotas.map((cota) => ({ ...TELAS[cota], cota, modulo: modulo.titulo }));
}

/**
 * O menu já recortado pelo que o usuário pode ver.
 *
 * Uma seção sem nenhum item liberado some inteira, título incluído: um cabeçalho
 * "Financeiro" sozinho anuncia um módulo que o usuário não alcança.
 */
export function modulosVisiveis(
  podeVer: (permissao: string) => boolean,
): { titulo: string; itens: ItemDeTela[] }[] {
  return MODULOS.map((modulo) => ({
    titulo: modulo.titulo,
    itens: itensDoModulo(modulo).filter((item) => podeVer(item.permissao)),
  })).filter((modulo) => modulo.itens.length > 0);
}

/* -------------------------- Favoritos e submenus -------------------------- */

/** Os submenus de uma seção, na ordem em que aparecem. */
export const SUBMENUS = [
  { natureza: 'processo', titulo: 'Processos' },
  { natureza: 'cadastro', titulo: 'Cadastros' },
  { natureza: 'relatorio', titulo: 'Relatórios' },
] as const satisfies readonly { natureza: Exclude<Natureza, 'painel'>; titulo: string }[];

export type TituloDeSubmenu = (typeof SUBMENUS)[number]['titulo'];

/**
 * A partir de quantas telas visíveis a seção ganha submenus.
 *
 * O mesmo cinco que decide se o menu tem seções (ver `MODULOS`): até quatro, a
 * lista corrida se lê de relance, e um submenu com uma tela só custaria um
 * clique para não agrupar nada. Recebimento, com quatro telas, continua plano;
 * Pagamento chegou a cinco com o agrupamento de títulos (`PAG·05`) e ganhou
 * submenus — e a contagem é do que **o usuário vê**, não do catálogo:
 * a seção de Compras de quem só enxerga três telas também fica plana.
 */
export const MINIMO_PARA_SUBMENUS = 5;

export interface Submenu {
  readonly titulo: TituloDeSubmenu;
  readonly itens: ItemDeTela[];
}

/**
 * Uma seção do menu pronta para desenhar (`.spec/015`, decisões 75 e 105).
 *
 * De cima para baixo: o painel, e o resto — em submenus por natureza quando a
 * seção é longa, em lista corrida quando é curta.
 *
 * **A favorita continua no lugar dela.** Até a decisão 105 ela saía do submenu
 * e subia para o topo da própria seção; com o menu de Favoritos, que junta as
 * de todos os módulos, tirá-la do módulo faria a tela sumir de onde ela sempre
 * esteve para quem procura pelo caminho.
 */
export interface SecaoDoMenu {
  readonly titulo: string;
  /** Sempre no topo: é a tela que decide qual abrir. */
  readonly paineis: ItemDeTela[];
  /** Vazio na seção curta. Só os submenus que têm alguma tela. */
  readonly submenus: Submenu[];
  /** Na seção curta, o que não é painel. Vazio na seção longa. */
  readonly demais: ItemDeTela[];
  /** Todas as telas visíveis da seção — é por ela que se acha a tela aberta. */
  readonly itens: ItemDeTela[];
}

/** Monta a seção a partir das telas que o usuário vê. */
export function montarSecao(modulo: { titulo: string; itens: ItemDeTela[] }): SecaoDoMenu {
  const paineis = modulo.itens.filter((item) => item.natureza === 'painel');
  const restantes = modulo.itens.filter((item) => item.natureza !== 'painel');

  const curta = modulo.itens.length < MINIMO_PARA_SUBMENUS;

  return {
    titulo: modulo.titulo,
    paineis,
    submenus: curta
      ? []
      : SUBMENUS.map((submenu) => ({
          titulo: submenu.titulo,
          itens: restantes.filter((item) => item.natureza === submenu.natureza),
        })).filter((submenu) => submenu.itens.length > 0),
    demais: curta ? restantes : [],
    itens: modulo.itens,
  };
}

/** O menu inteiro, já recortado pelo papel. */
export function secoesDoMenu(podeVer: (permissao: string) => boolean): SecaoDoMenu[] {
  return modulosVisiveis(podeVer).map((modulo) => montarSecao(modulo));
}

/**
 * O menu de Favoritos: as telas marcadas, **de todos os módulos**, na ordem do
 * vetor gravado (`.spec/015`, decisão 105).
 *
 * Uma lista só, e não um bloco por módulo: quem favorita "Pedidos de compra" e
 * "Contas a pagar" quer as duas a um clique, e não abrir Compras e Pagamento
 * para achá-las no topo de cada um.
 *
 * Cota que o papel não abre, que saiu do catálogo ou que é painel
 * simplesmente não casa — é assim que o favorito órfão some sem que ninguém
 * precise apagá-lo, e volta se o papel voltar.
 */
export function favoritosDoMenu(
  podeVer: (permissao: string) => boolean,
  favoritos: readonly string[],
): ItemDeTela[] {
  // Chave `string`: o vetor gravado traz qualquer texto no formato da cota,
  // inclusive a que saiu do catálogo.
  const porCota = new Map<string, ItemDeTela>(
    modulosVisiveis(podeVer)
      .flatMap((modulo) => modulo.itens)
      .filter((item) => item.natureza !== 'painel')
      .map((item) => [item.cota, item]),
  );

  return favoritos.flatMap((cota) => {
    const item = porCota.get(cota);
    return item === undefined ? [] : [item];
  });
}

/**
 * O módulo em que a cota mora. `null` para cota fora do catálogo.
 *
 * É o "mesmo grupo" de `moverFavorito`: a seta de subir troca a tela com a
 * favorita anterior **da mesma seção**, e não com a vizinha do vetor gravado.
 */
export function moduloDaCota(cota: string): string | null {
  return MODULOS.find((secao) => secao.cotas.some((c) => c === cota))?.titulo ?? null;
}

/**
 * O caminho aberto agora pertence a esta tela?
 *
 * A rota exata ou um filho dela (`/obras/novo`, `/obras/<id>`), e **não**
 * `startsWith` cru: `/conta` — a configuração da conta — é prefixo de
 * `/contas-pagar`, de `/contas-receber` e de `/contas-correntes`. Com a
 * comparação ingênua, lançar um título a pagar acendia "Configuração" no menu de
 * Administração, e o usuário via duas telas abertas ao mesmo tempo.
 */
export function telaAberta(caminho: string, para: string): boolean {
  return caminho === para || caminho.startsWith(`${para}/`);
}

/**
 * Que tela está aberta agora. `null` fora do menu (login, erro, console).
 *
 * Ganha a rota **mais específica** entre as que casam: `/notas-compra/por-pedido`
 * é filha de `/notas-compra`, e as duas respondem que sim. Sem o desempate o
 * menu acende dois itens, e nenhum dos dois é onde o usuário está.
 */
export function cotaDoCaminho(caminho: string): Cota | null {
  let escolhida: Cota | null = null;

  for (const cota of Object.keys(TELAS) as Cota[]) {
    if (!telaAberta(caminho, TELAS[cota].para)) continue;
    if (escolhida === null || TELAS[cota].para.length > TELAS[escolhida].para.length) {
      escolhida = cota;
    }
  }

  return escolhida;
}

/** Qual seção contém o caminho aberto agora. `null` fora do menu. */
export function moduloDoCaminho(caminho: string): string | null {
  const aberta = cotaDoCaminho(caminho);
  if (aberta === null) return null;

  return MODULOS.find((secao) => secao.cotas.some((cota) => cota === aberta))?.titulo ?? null;
}

/**
 * Reduz o texto ao que interessa para comparar: sem acento, sem caixa e sem
 * pontuação.
 *
 * É o que faz `cmp01`, `CMP 01` e `cmp·01` acharem a mesma tela — o ponto médio
 * não está em teclado nenhum, e cobrar dele seria transformar a busca em
 * adivinhação. Pelo mesmo motivo "cotacoes" acha "Cotações de preço": quem está
 * com pressa não digita acento.
 */
function chave(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}

/**
 * Busca por cota, por nome da tela ou por nome do módulo.
 *
 * O módulo entra no que se procura porque "estoque" é o jeito como metade dos
 * usuários chama o assunto, e nenhum item da seção tem essa palavra no rótulo.
 *
 * Termo vazio devolve lista vazia, e não tudo: quem não digitou nada quer o menu
 * de sempre, com as seções que ele fechou continuando fechadas.
 *
 * O recorte de permissão é o mesmo do menu — a busca não é uma porta lateral
 * para telas que o usuário não pode abrir.
 */
export function buscarTelas(termo: string, podeVer: (permissao: string) => boolean): ItemDeTela[] {
  const procurado = chave(termo);
  if (procurado === '') return [];

  return modulosVisiveis(podeVer)
    .flatMap((modulo) => modulo.itens)
    .filter(
      (item) =>
        chave(item.cota).includes(procurado) ||
        chave(item.rotulo).includes(procurado) ||
        chave(item.modulo).includes(procurado),
    );
}
